// src/context/CustomerContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { customerService } from '../services/customerService';
import { AuthContext } from './AuthContext';
import cartManager from '../services/CartManager';
import logger from '../utils/logger';

const CustomerContext = createContext();

export const useCustomerContext = () => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomerContext must be used within a CustomerContextProvider');
  }
  return context;
};

const CUSTOMER_CONTEXT_KEY = 'selectedCustomerContext';

export const CustomerContextProvider = ({ children }) => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if user is a customer (linked to a specific customer)
  const isCustomerUser = user && !user.is_staff && user.customer_profile;
  
  // Search customers based on search term - optimized for performance
  const searchCustomers = useCallback(async (searchTerm = '') => {
    if (!isAuthenticated || !user || isCustomerUser) return [];

    try {
      setLoading(true);
      setError(null);

      logger.log('[CustomerContext] Searching customers with term:', searchTerm);
      
      const result = await customerService.getCustomers({
        search: searchTerm,
        page_size: 100 // Reasonable limit for search results
      });
      
      if (result.success) {
        const customers = result.data?.results || result.data || [];
        logger.log('[CustomerContext] Search results:', customers.length, 'customers found');
        return Array.isArray(customers) ? customers : [];
      } else {
        console.error('[CustomerContext] Failed to search customers:', result.error);
        setError(result.error || 'Failed to search customers');
        return [];
      }
    } catch (err) {
      console.error('Error searching customers:', err);
      setError('Failed to search customers');
      return [];
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, isCustomerUser]);

  // Load initial customers with minimal data - optimized for performance  
  const loadAvailableCustomers = useCallback(async (limit = 50) => {
    if (!isAuthenticated || !user) return;

    try {
      setLoading(true);
      setError(null);

      // For customer users, no need to load customers - they're auto-assigned
      if (isCustomerUser) {
        setAvailableCustomers([]);
        return;
      }

      // For staff users, load only recent/active customers initially
      logger.log('[CustomerContext] Loading initial customers for staff user...');
      
      const result = await customerService.getCustomers({
        page_size: limit,
        ordering: '-created_at' // Get most recent customers first
      });
      logger.log('[CustomerContext] Customer service result:', result);
      
      if (result.success) {
        const customers = result.data?.results || result.data || [];
        logger.log('[CustomerContext] Initial customers loaded:', customers.length);
        
        setAvailableCustomers(Array.isArray(customers) ? customers : []);
      } else {
        console.error('[CustomerContext] Failed to load customers:', result.error);
        setError(result.error || 'Failed to load customers');
        setAvailableCustomers([]);
      }
    } catch (err) {
      console.error('Error loading customers:', err);
      setError('Failed to load customers');
      setAvailableCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, isCustomerUser]);

  // Auto-select customer for customer users
  useEffect(() => {
    if (isCustomerUser && user.customer_profile) {
      const customerId = user.customer_profile.id;
      setSelectedCustomer({
        id: customerId,
        name: user.customer_profile.name,
        autoSelected: true
      });
      // Set customer ID on CartManager for discount calculations
      cartManager.setCustomerId(customerId);
    }
  }, [isCustomerUser, user]);

  // Load customers for staff users
  useEffect(() => {
    if (isAuthenticated && !isCustomerUser) {
      loadAvailableCustomers();
    }
  }, [isAuthenticated, isCustomerUser, loadAvailableCustomers]);

  // Restore selected customer from localStorage for staff users
  useEffect(() => {
    if (!isCustomerUser && isAuthenticated && user) {
      try {
        const saved = localStorage.getItem(CUSTOMER_CONTEXT_KEY);
        if (saved) {
          const savedCustomer = JSON.parse(saved);
          // Restore the saved customer directly - it will be validated when accessed
          setSelectedCustomer(savedCustomer);
          // Also set the customer ID on CartManager for discount calculations
          cartManager.setCustomerId(savedCustomer?.id || null);
          logger.log('[CustomerContext] Restored customer from localStorage:', savedCustomer.name);
        }
      } catch (err) {
        console.error('Error restoring customer context:', err);
      }
    }
  }, [isCustomerUser, isAuthenticated, user]);

  // Save selected customer to localStorage and update cart manager
  const selectCustomer = useCallback((customer) => {
    setSelectedCustomer(customer);

    // Update CartManager with the selected customer ID for discount calculations
    cartManager.setCustomerId(customer?.id || null);

    // Recalculate prices and discounts with new customer
    // recalculatePricesForCustomer updates base prices then calls calculateDiscounts
    if (cartManager.getCart().items?.length > 0) {
      cartManager.recalculatePricesForCustomer(customer?.id || null);
    }

    if (!isCustomerUser && customer) {
      try {
        localStorage.setItem(CUSTOMER_CONTEXT_KEY, JSON.stringify(customer));
      } catch (err) {
        console.error('Error saving customer context:', err);
      }
    }
  }, [isCustomerUser]);

  // Clear selected customer
  const clearCustomer = useCallback(() => {
    setSelectedCustomer(null);

    // Clear CartManager customer ID and recalculate prices/discounts
    cartManager.setCustomerId(null);
    if (cartManager.getCart().items?.length > 0) {
      cartManager.recalculatePricesForCustomer(null);
    }

    if (!isCustomerUser) {
      try {
        localStorage.removeItem(CUSTOMER_CONTEXT_KEY);
      } catch (err) {
        console.error('Error clearing customer context:', err);
      }
    }
  }, [isCustomerUser]);

  // Track if we've ever been authenticated (to distinguish initial load from logout)
  const [wasAuthenticated, setWasAuthenticated] = useState(false);

  // Track authentication state changes
  useEffect(() => {
    if (isAuthenticated) {
      setWasAuthenticated(true);
    }
  }, [isAuthenticated]);

  // Clear context on logout (not on initial load)
  useEffect(() => {
    if (!isAuthenticated && wasAuthenticated) {
      setSelectedCustomer(null);
      setAvailableCustomers([]);
      try {
        localStorage.removeItem(CUSTOMER_CONTEXT_KEY);
      } catch (err) {
        console.error('Error clearing customer context on logout:', err);
      }
    }
  }, [isAuthenticated, wasAuthenticated]);

  const value = {
    selectedCustomer,
    availableCustomers: Array.isArray(availableCustomers) ? availableCustomers : [],
    loading,
    error,
    isCustomerUser,
    selectCustomer,
    clearCustomer,
    refreshCustomers: loadAvailableCustomers,
    searchCustomers // Add search function to context
  };

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
};

export default CustomerContext;